/**
 * useVideoCall Hook
 * Production-ready video call integration using Agora Web SDK
 */

import { useState, useRef, useCallback, useEffect } from "react";
import AgoraRTC, {
  IAgoraRTCClient,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteVideoTrack,
  IAgoraRTCRemoteUser,
  NetworkQuality as AgoraNetworkQuality,
} from "agora-rtc-sdk-ng";
import type {
  NetworkQuality,
  VideoCallConfig,
  VideoConnectionState,
  UseVideoCallReturn,
} from "@/types/video-call";

// Agora App ID - same as Flutter implementation
const AGORA_APP_ID = "5eaa7fbea8854d37bf985748731fc93f";

// Map Agora network quality to our format
const mapNetworkQuality = (quality: number): NetworkQuality => {
  switch (quality) {
    case 1:
      return "excellent";
    case 2:
      return "good";
    case 3:
    case 4:
      return "poor";
    case 5:
      return "bad";
    case 6:
      return "very-bad";
    case 0:
    default:
      return "unknown";
  }
};

// Format duration as MM:SS
const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

export const useVideoCall = (config: VideoCallConfig): UseVideoCallReturn => {
  const { onCallEnd, onRemoteUserJoined, onRemoteUserLeft, onError } = config;

  // Refs
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const localVideoTrackRef = useRef<ICameraVideoTrack | null>(null);
  const localAudioTrackRef = useRef<IMicrophoneAudioTrack | null>(null);
  const remoteVideoTrackRef = useRef<IRemoteVideoTrack | null>(null);
  const localVideoElRef = useRef<HTMLDivElement | null>(null);
  const remoteVideoRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const secondsElapsedRef = useRef(0);
  const channelInfoRef = useRef<{ token: string; channelId: string } | null>(
    null
  );

  // State
  const [isJoined, setIsJoined] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isCallEnded, setIsCallEnded] = useState(false);
  const [localUserJoined, setLocalUserJoined] = useState(false);
  const [localUserId, setLocalUserId] = useState<number | null>(null);
  const [remoteUserJoined, setRemoteUserJoined] = useState(false);
  const [remoteUserId, setRemoteUserId] = useState<number | null>(null);
  const [networkQuality, setNetworkQuality] =
    useState<NetworkQuality>("unknown");
  const [connectionState, setConnectionState] =
    useState<VideoConnectionState>("disconnected");
  const [callDuration, setCallDuration] = useState("00:00");
  const [error, setError] = useState<string | null>(null);

  const syncRemoteUser = useCallback(
    (user: IAgoraRTCRemoteUser) => {
      setRemoteUserJoined(true);
      setRemoteUserId(user.uid as number);
      onRemoteUserJoined?.(user.uid as number);
    },
    [onRemoteUserJoined]
  );

  // Initialize Agora client
  const initClient = useCallback(() => {
    if (clientRef.current) return clientRef.current;

    const client = AgoraRTC.createClient({
      mode: "rtc",
      codec: "vp8",
    });

    // Network quality event
    client.on("network-quality", (stats: AgoraNetworkQuality) => {
      setNetworkQuality(mapNetworkQuality(stats.uplinkNetworkQuality));
    });

    // Remote user events
    client.on("user-joined", (user: IAgoraRTCRemoteUser) => {
      syncRemoteUser(user);
    });

    client.on(
      "user-published",
      async (user: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
        await client.subscribe(user, mediaType);
        syncRemoteUser(user);

        if (mediaType === "video") {
          // Store the track - will be played in useEffect when ref is ready
          remoteVideoTrackRef.current = user.videoTrack || null;

          // Try to play immediately if ref is ready
          if (remoteVideoRef.current && user.videoTrack) {
            user.videoTrack.play(remoteVideoRef.current);
          }
        }

        if (mediaType === "audio") {
          user.audioTrack?.play();
        }
      }
    );

    client.on(
      "user-unpublished",
      (user: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
        if (mediaType === "video") {
          user.videoTrack?.stop();
          remoteVideoTrackRef.current = null;
        }
        if (mediaType === "audio") {
          user.audioTrack?.stop();
        }
      }
    );

    client.on("user-left", (user: IAgoraRTCRemoteUser) => {
      setRemoteUserJoined(false);
      setRemoteUserId(null);
      remoteVideoTrackRef.current = null;
      onRemoteUserLeft?.(user.uid as number);
    });

    client.on("connection-state-change", (curState, prevState) => {
      console.log(`Connection state changed from ${prevState} to ${curState}`);
      switch (curState) {
        case "CONNECTING":
          setConnectionState("connecting");
          break;
        case "CONNECTED":
          setConnectionState("connected");
          break;
        case "RECONNECTING":
          setConnectionState("reconnecting");
          break;
        case "DISCONNECTED":
        case "DISCONNECTING":
        case "DISCONNECTED_ABORT":
        default:
          setConnectionState("disconnected");
          break;
      }
    });

    clientRef.current = client;
    return client;
  }, [onRemoteUserLeft, syncRemoteUser]);

  // Start call timer
  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      secondsElapsedRef.current += 1;
      setCallDuration(formatDuration(secondsElapsedRef.current));
    }, 1000);
  }, []);

  // Stop call timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Join channel
  const joinChannel = useCallback(
    async (token: string, channelId: string) => {
      try {
        setIsConnecting(true);
        setError(null);
        setIsCallEnded(false);
        setConnectionState("connecting");
        secondsElapsedRef.current = 0;
        setCallDuration("00:00");

        const client = initClient();
        channelInfoRef.current = { token, channelId };

        // Request camera and microphone permissions separately for better control
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
          encoderConfig: "high_quality",
        });

        const videoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: {
            width: 1280,
            height: 720,
            frameRate: 30,
            bitrateMax: 1500,
          },
        });

        localVideoTrackRef.current = videoTrack;
        localAudioTrackRef.current = audioTrack;

        // Play local video preview
        if (localVideoElRef.current) {
          videoTrack.play(localVideoElRef.current);
        }

        // Join the channel
        const uid = await client.join(AGORA_APP_ID, channelId, token, null);

        // Publish local tracks
        await client.publish([audioTrack, videoTrack]);

        setIsJoined(true);
        setLocalUserJoined(true);
        setLocalUserId(uid as number);
        setIsVideoEnabled(true);
        setIsAudioEnabled(true);
        setIsMuted(false);
        setIsConnecting(false);
        setConnectionState("connected");
        startTimer();

        const existingRemoteUser = client.remoteUsers.find((user) =>
          Boolean(user.hasAudio || user.hasVideo || user.uid)
        );
        if (existingRemoteUser) {
          syncRemoteUser(existingRemoteUser);
        }

        console.log("Successfully joined channel:", channelId);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to join call");

        console.error("Failed to join channel:", error);
        stopTimer();

        if (localVideoTrackRef.current) {
          localVideoTrackRef.current.stop();
          localVideoTrackRef.current.close();
          localVideoTrackRef.current = null;
        }

        if (localAudioTrackRef.current) {
          localAudioTrackRef.current.stop();
          localAudioTrackRef.current.close();
          localAudioTrackRef.current = null;
        }

        if (remoteVideoTrackRef.current) {
          remoteVideoTrackRef.current.stop();
          remoteVideoTrackRef.current = null;
        }

        if (clientRef.current) {
          try {
            await clientRef.current.leave();
          } catch (leaveError) {
            console.error(
              "Failed to leave channel after join error:",
              leaveError
            );
          }
        }

        channelInfoRef.current = null;
        setIsJoined(false);
        setLocalUserJoined(false);
        setLocalUserId(null);
        setRemoteUserJoined(false);
        setRemoteUserId(null);
        setConnectionState("disconnected");
        setIsConnecting(false);
        setError(error.message || "Failed to join call");
        onError?.(error);
        throw error;
      }
    },
    [initClient, onError, startTimer, stopTimer, syncRemoteUser]
  );

  // Leave channel
  const leaveChannel = useCallback(async () => {
    try {
      stopTimer();

      // Stop and close local tracks
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
        localVideoTrackRef.current = null;
      }

      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
        localAudioTrackRef.current = null;
      }

      // Leave the channel
      if (clientRef.current) {
        await clientRef.current.leave();
      }

      setIsJoined(false);
      setConnectionState("disconnected");
      setLocalUserJoined(false);
      setLocalUserId(null);
      setRemoteUserJoined(false);
      setRemoteUserId(null);
      setIsCallEnded(true);
      setIsVideoEnabled(false);
      setIsAudioEnabled(false);
      setIsMuted(false);

      onCallEnd?.();
      console.log("Successfully left channel");
    } catch (err) {
      console.error("Failed to leave channel:", err);
      setError((err as Error).message || "Failed to leave call");
    }
  }, [stopTimer, onCallEnd]);

  // Toggle video
  const toggleVideo = useCallback(async () => {
    try {
      if (localVideoTrackRef.current) {
        const newState = !isVideoEnabled;
        await localVideoTrackRef.current.setEnabled(newState);
        setIsVideoEnabled(newState);
      }
    } catch (err) {
      console.error("Failed to toggle video:", err);
      setError((err as Error).message || "Failed to toggle video");
    }
  }, [isVideoEnabled]);

  // Toggle audio (speaker)
  const toggleAudio = useCallback(async () => {
    try {
      // Toggle remote audio playback
      if (clientRef.current) {
        const remoteUsers = clientRef.current.remoteUsers;
        remoteUsers.forEach((user) => {
          if (user.audioTrack) {
            if (isAudioEnabled) {
              user.audioTrack.stop();
            } else {
              user.audioTrack.play();
            }
          }
        });
        setIsAudioEnabled(!isAudioEnabled);
      }
    } catch (err) {
      console.error("Failed to toggle audio:", err);
      setError((err as Error).message || "Failed to toggle audio");
    }
  }, [isAudioEnabled]);

  // Toggle mute (microphone)
  const toggleMute = useCallback(async () => {
    try {
      if (localAudioTrackRef.current) {
        const newState = !isMuted;
        await localAudioTrackRef.current.setEnabled(!newState);
        setIsMuted(newState);
      }
    } catch (err) {
      console.error("Failed to toggle mute:", err);
      setError((err as Error).message || "Failed to toggle mute");
    }
  }, [isMuted]);

  // Rejoin the call
  const rejoin = useCallback(async () => {
    if (channelInfoRef.current) {
      const { token, channelId } = channelInfoRef.current;
      secondsElapsedRef.current = 0;
      setCallDuration("00:00");
      await joinChannel(token, channelId);
    }
  }, [joinChannel]);

  // Callback ref for local video — replays the track whenever the DOM element
  // is (re-)mounted (e.g. portal toggle between maximized/minimized).
  const localVideoRef = useCallback(
    (node: HTMLDivElement | null) => {
      localVideoElRef.current = node;
      if (
        node &&
        localVideoTrackRef.current &&
        localUserJoined &&
        isVideoEnabled
      ) {
        localVideoTrackRef.current.play(node);
      }
    },
    [localUserJoined, isVideoEnabled]
  );

  // Play remote video when ref becomes available after render
  useEffect(() => {
    if (
      remoteUserJoined &&
      remoteVideoRef.current &&
      remoteVideoTrackRef.current
    ) {
      console.log("Playing remote video track on ref");
      remoteVideoTrackRef.current.play(remoteVideoRef.current);
    }
  }, [remoteUserJoined]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();

      // Cleanup tracks
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
      }
      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
      }
      if (remoteVideoTrackRef.current) {
        remoteVideoTrackRef.current.stop();
      }

      // Leave channel
      if (clientRef.current) {
        clientRef.current.leave().catch(console.error);
      }
    };
  }, [stopTimer]);

  return {
    // State
    isJoined,
    isConnecting,
    isVideoEnabled,
    isAudioEnabled,
    isMuted,
    isCallEnded,
    localUserJoined,
    localUserId,
    remoteUserJoined,
    remoteUserId,
    networkQuality,
    connectionState,
    callDuration,
    error,

    // Actions
    joinChannel,
    leaveChannel,
    toggleVideo,
    toggleAudio,
    toggleMute,
    rejoin,

    // Refs
    localVideoRef,
    remoteVideoRef,
  };
};

export default useVideoCall;
